import { useEffect, useMemo, useState } from "react";
import MoonMark from "@/components/MoonMark";

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICES = {
  soundtrack: "Multimedia Original Soundtrack",
  ttrpg: "Tabletop RPG Background Music",
  gdd_review: "Game Design Document Review",
  gdd_writing: "Game Design Document Writing",
  direction: "Creative Direction",
};
const SCALES = ["Sketch", "Short", "Standard", "Extended", "Full Saga"];
const DEADLINES = { flexible: "Flexible · 60+ days", standard: "Standard · ~30 days", tight: "Tight · under 14 days" };
const REVISIONS = { one: "One round", three: "Three rounds", unlimited: "Unlimited" };
const LICENSING = { indie: "Indie / Personal", commercial: "Commercial", worldwide: "Worldwide · All media" };

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const toDraft = (cfg) => ({
  bases: Object.fromEntries(Object.keys(SERVICES).map((k) => [k, String(cfg.service_bases?.[k] ?? "")])),
  scale: (cfg.scale_mult || []).map((v) => String(v)),
  deadline: Object.fromEntries(Object.keys(DEADLINES).map((k) => [k, String(cfg.deadline_mult?.[k] ?? "")])),
  revision: Object.fromEntries(Object.keys(REVISIONS).map((k) => [k, String(cfg.revision_mult?.[k] ?? "")])),
  licensing: Object.fromEntries(Object.keys(LICENSING).map((k) => [k, String(cfg.licensing_mult?.[k] ?? "")])),
  rushPct: String(Math.round(((cfg.rush_mult ?? 1) - 1) * 100)),
});

const num = (s, fb) => {
  const v = parseFloat(s);
  return Number.isFinite(v) && v > 0 ? v : fb;
};

const NumRow = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between gap-4 border border-line bg-surface px-4 py-3">
    <p className="text-sm font-light text-slate-300">{label}</p>
    <input
      type="number"
      step="any"
      min="0"
      className="input-night !w-28 !py-2 text-right"
      value={value}
      onChange={onChange}
      data-testid={testid}
    />
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <p className="mb-3 font-jmono text-[10px] uppercase tracking-[0.3em] text-gold">{title}</p>
    <div className="space-y-2">{children}</div>
  </div>
);

const AdminPricing = () => {
  const [draft, setDraft] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setError("");
    try {
      const res = await fetch(`${API}/api/quote-config`);
      if (!res.ok) throw new Error();
      setDraft(toDraft(await res.json()));
    } catch {
      setError("Could not load the current pricing.");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upd = (section, key) => (e) => {
    const v = e.target.value;
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: v } }));
    setSaved(false);
  };
  const updScale = (i) => (e) => {
    const v = e.target.value;
    setDraft((d) => ({ ...d, scale: d.scale.map((x, j) => (j === i ? v : x)) }));
    setSaved(false);
  };

  const preview = useMemo(() => {
    if (!draft) return null;
    const raw =
      num(draft.bases.soundtrack, 2500) *
      num(draft.scale[2], 2.4) *
      num(draft.deadline.standard, 1.15) *
      num(draft.revision.three, 1.15) *
      num(draft.licensing.indie, 1);
    return Math.round(raw / 50) * 50;
  }, [draft]);

  const save = async () => {
    setError("");
    setSaved(false);
    if (!draft) return;
    const missing = [];
    const bases = {};
    Object.keys(SERVICES).forEach((k) => {
      const v = parseFloat(draft.bases[k]);
      if (!(v > 0)) missing.push(SERVICES[k]);
      else bases[k] = Math.round(v * 100) / 100;
    });
    const scale = draft.scale.map((s, i) => {
      const v = parseFloat(s);
      if (!(v > 0)) missing.push(`${SCALES[i]} multiplier`);
      return v;
    });
    const group = (obj, labels) =>
      Object.fromEntries(
        Object.entries(obj).map(([k, s]) => {
          const v = parseFloat(s);
          if (!(v > 0)) missing.push(`${labels[k]} multiplier`);
          return [k, v];
        })
      );
    const rush = parseFloat(draft.rushPct);
    if (!(rush >= 0)) missing.push("Rush fee");
    if (missing.length) {
      setError(`Enter a valid number for: ${[...new Set(missing)].join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const token = localStorage.getItem("nox_admin_token") || "";
      const res = await fetch(`${API}/api/admin/quote-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({
          service_bases: bases,
          scale_mult: scale,
          deadline_mult: group(draft.deadline, DEADLINES),
          revision_mult: group(draft.revision, REVISIONS),
          licensing_mult: group(draft.licensing, LICENSING),
          rush_mult: Math.round((1 + rush / 100) * 10000) / 10000,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Save failed");
      setSaved(true);
    } catch (err) {
      setError(err.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) {
    return (
      <div className="mt-10 flex items-center justify-center gap-4 py-10">
        <MoonMark className="h-6 w-6 animate-spin text-gold" style={{ animationDuration: "6s" }} />
        <span className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Summoning…</span>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="mt-10 border border-line bg-surface p-8 text-center" data-testid="admin-pricing">
        <p className="text-xs text-red-500" data-testid="pricing-error">
          {error}
        </p>
        <button className="btn-ghost-night mt-4" onClick={load} data-testid="pricing-retry">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="mt-10 space-y-8" data-testid="admin-pricing">
      <p className="max-w-2xl text-xs leading-relaxed text-slate-500">
        These numbers drive the public quote calculator on the Services page. Labels and tiers are fixed — here
        you tune every base price, multiplier and the rush fee. Changes apply the next time the calculator loads.
      </p>

      <Section title="Service base prices (USD)">
        {Object.entries(SERVICES).map(([k, label]) => (
          <NumRow
            key={k}
            label={label}
            value={draft.bases[k]}
            onChange={upd("bases", k)}
            testid={`pricing-base-${k}`}
          />
        ))}
      </Section>

      <Section title="Project scale multipliers">
        {SCALES.map((label, i) => (
          <NumRow
            key={label}
            label={label}
            value={draft.scale[i] ?? ""}
            onChange={updScale(i)}
            testid={`pricing-scale-${i}`}
          />
        ))}
      </Section>

      <Section title="Deadline multipliers">
        {Object.entries(DEADLINES).map(([k, label]) => (
          <NumRow
            key={k}
            label={label}
            value={draft.deadline[k]}
            onChange={upd("deadline", k)}
            testid={`pricing-deadline-${k}`}
          />
        ))}
      </Section>

      <Section title="Revision multipliers">
        {Object.entries(REVISIONS).map(([k, label]) => (
          <NumRow
            key={k}
            label={label}
            value={draft.revision[k]}
            onChange={upd("revision", k)}
            testid={`pricing-revision-${k}`}
          />
        ))}
      </Section>

      <Section title="Licensing multipliers">
        {Object.entries(LICENSING).map(([k, label]) => (
          <NumRow
            key={k}
            label={label}
            value={draft.licensing[k]}
            onChange={upd("licensing", k)}
            testid={`pricing-licensing-${k}`}
          />
        ))}
      </Section>

      <Section title="Rush fee">
        <NumRow
          label="Rush fee"
          value={draft.rushPct}
          onChange={(e) => {
            const v = e.target.value;
            setDraft((d) => ({ ...d, rushPct: v }));
            setSaved(false);
          }}
          testid="pricing-rush"
        />
      </Section>

      <div className="border border-gold/30 bg-[#0d0d10] p-6 text-center">
        <p className="font-jmono text-[10px] uppercase tracking-[0.35em] text-slate-500">Sample estimate</p>
        <p className="mt-2 text-xs text-slate-600">
          Soundtrack · Standard scale · Standard deadline · Three rounds · Indie — the visitor's default view
        </p>
        <p className="mt-3 font-display text-3xl text-gold" data-testid="pricing-preview">
          {preview ? fmt(preview) : "—"}
        </p>
      </div>

      {error && (
        <p className="text-xs text-red-500" data-testid="pricing-error">
          {error}
        </p>
      )}
      {saved && (
        <p className="text-xs text-emerald-400" data-testid="pricing-saved">
          Saved. The public calculator now uses these numbers.
        </p>
      )}

      <button className="btn-night" onClick={save} disabled={busy} data-testid="pricing-save">
        {busy ? "Saving…" : "Save Pricing"}
      </button>
    </div>
  );
};

export default AdminPricing;
